"use client";

import React, { useState } from "react";
import { useRouter } from 'next/navigation';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OTPInput } from "@/components/ui/otp-input";
import { sendOTP, verifyOTP } from "@/actions/auth";
import { toast } from "sonner";
import { Mail, ArrowLeft, RefreshCw, AlertCircle } from "lucide-react";
import Link from "next/link";

export function EmailLoginForm() {
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [isCodeSent, setIsCodeSent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [errors, setErrors] = useState<{ email?: string; otp?: string }>({});
    const [touched, setTouched] = useState<{ email?: boolean; otp?: boolean }>({});
    const [hasAttemptedSignup, setHasAttemptedSignup] = useState(false);
    const router = useRouter();

    // Validation functions
    const validateEmail = (value: string): string | undefined => {
        if (!value.trim()) {
            return 'Email address is required';
        }
        
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
            return 'Please enter a valid email address';
        }
        return undefined;
    };

    const validateOTP = (value: string): string | undefined => {
        if (!value.trim()) {
            return 'Verification code is required';
        }
        if (value.length !== 6) {
            return 'Please enter the complete 6-digit code';
        }
        return undefined;
    };

    const handleEmailChange = (value: string) => {
        setEmail(value);
        setTouched(prev => ({ ...prev, email: true }));
        
        // Clear error when user starts typing
        if (errors.email) {
            setErrors(prev => ({ ...prev, email: undefined }));
        }
        
        // Reset attempt flag when user changes email
        if (hasAttemptedSignup) {
            setHasAttemptedSignup(false);
        }
    };

    const handleOTPChange = (value: string) => {
        setOtp(value);
        setTouched(prev => ({ ...prev, otp: true }));
        
        // Clear error when user starts typing
        if (errors.otp) {
            setErrors(prev => ({ ...prev, otp: undefined }));
        }
    };

    const handleSendCode = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validate email field
        const emailError = validateEmail(email);
        if (emailError) {
            setErrors(prev => ({ ...prev, email: emailError }));
            setTouched(prev => ({ ...prev, email: true }));
            return;
        }
        
        setIsLoading(true);

        try {
            await sendOTP({ email });
            toast.success("A 6-digit code has been sent to your email!");
            setIsCodeSent(true);
            setCountdown(30);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : `Failed to send code to ${email}`;
            
            // Mark that user has attempted signup
            setHasAttemptedSignup(true);
            
            // Check if this is the email signup disabled error
            if (errorMessage.includes("Email signup is currently disabled")) {
                // Show a more prominent error message with additional context
                toast.error("Email signup is currently disabled", {
                    duration: 8000, // Show longer
                    description: "Please use phone number to create an account or contact support if you have an existing email account."
                });
            } else {
                toast.error(errorMessage);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyCode = async () => {
        // Validate OTP
        const otpError = validateOTP(otp);
        if (otpError) {
            setErrors(prev => ({ ...prev, otp: otpError }));
            setTouched(prev => ({ ...prev, otp: true }));
            return;
        }
        
        setIsVerifying(true);

        try {
            await verifyOTP({ email, token: otp });
            toast.success("Successfully signed in! Redirecting...");
            router.push('/home');
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Invalid code. Please try again.";
            toast.error(errorMessage);
            setOtp("");
        } finally {
            setIsVerifying(false);
        }
    };

    const handleResendCode = async () => {
        if (countdown > 0) return;
        setIsLoading(true);

        try {
            await sendOTP({ email });
            setCountdown(30);
            toast.success("New code sent!");
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Failed to send new code";
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    // Countdown effect
    React.useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    if (isCodeSent) {
        return (
            <div className="w-full max-w-md mx-auto space-y-6">
                {/* Header */}
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-gray-900 mb-1">Enter verification code</h2>
                    <p className="text-sm text-gray-600">Sent to {email}</p>
                </div>

                {/* OTP Form */}
                <div className="space-y-4">
                    <OTPInput
                        value={otp}
                        onChange={handleOTPChange}
                        disabled={isVerifying}
                        length={6}
                        className={touched.otp && errors.otp ? 'border-red-300' : ''}
                    />
                    
                    {touched.otp && errors.otp && (
                        <p className="text-sm text-red-600 flex items-center justify-center gap-1">
                            <AlertCircle className="h-4 w-4" />
                            {errors.otp}
                        </p>
                    )}

                    <Button
                        className="w-full h-12 text-base font-medium"
                        onClick={handleVerifyCode}
                        disabled={isVerifying || otp.length !== 6}
                    >
                        {isVerifying ? (
                            <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Verifying...
                            </>
                        ) : (
                            'Sign In'
                        )}
                    </Button>
                </div>

                {/* Resend */}
                <div className="text-center">
                    <button
                        onClick={handleResendCode}
                        disabled={countdown > 0 || isLoading}
                        className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                        {isLoading ? (
                            "Sending..."
                        ) : countdown > 0 ? (
                            `Resend in ${countdown}s`
                        ) : (
                            'Resend code'
                        )}
                    </button>
                </div>

                {/* Back to email input */}
                <div className="text-center">
                    <button
                        onClick={() => {
                            setIsCodeSent(false);
                            setOtp('');
                            setErrors({});
                            setTouched({});
                            setHasAttemptedSignup(false);
                        }}
                        className="text-sm text-gray-600 hover:text-gray-800 flex items-center justify-center mx-auto"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Use different email
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-md mx-auto space-y-6">
            {/* Header */}
            <div className="text-center">
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">Sign in with Email</h2>
                <p className="text-gray-600">For existing email accounts only</p>
                {hasAttemptedSignup && (
                    <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                        <p className="text-sm text-amber-800">
                            <strong>Note:</strong> New account creation with email is currently disabled. 
                            Please use phone number to create a new account.
                        </p>
                    </div>
                )}
            </div>

            {/* Email Input Form */}
            <form onSubmit={handleSendCode} className="space-y-4">
                <div className="space-y-2">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                        Email address
                    </label>
                    
                    <Input
                        id="email"
                        type="email"
                        placeholder="name@example.com"
                        value={email}
                        onChange={(e) => handleEmailChange(e.target.value)}
                        disabled={isLoading}
                        autoComplete="email"
                        className={`h-12 text-base ${
                            touched.email && errors.email 
                                ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                        }`}
                        aria-invalid={touched.email && !!errors.email}
                    />

                    {touched.email && errors.email && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle className="h-4 w-4" />
                            {errors.email}
                        </p>
                    )}
                </div>

                <Button
                    type="submit"
                    disabled={isLoading || !email.trim()}
                    className="w-full h-12 text-base font-medium"
                >
                    {isLoading ? (
                        <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                        </>
                    ) : (
                        <>
                            <Mail className="w-4 h-4 mr-2" />
                            Send Code
                        </>
                    )}
                </Button>
            </form>

            {/* Back to phone signup */}
            <div className="text-center pt-4 border-t border-gray-200">
                <Link 
                    href="/auth"
                    className="text-sm text-gray-600 hover:text-gray-800 flex items-center justify-center mx-auto"
                >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Back to phone signup
                </Link>
            </div>
        </div>
    );
}
